import React, { useState, useEffect, useMemo, useCallback } from "react";
import { css } from "@emotion/react";
import { Title, Button, ConfirmModal, PageTable } from "@src/components";
import { useNavigate } from "react-router-dom";
import { useNoticeFetchData } from "@src/hooks/queries/useNotice";
import { useDeleteNotice } from "@src/hooks/queries/useNoticeDetail";
import { NoticeType } from "@src/types/NoticeType";
import { useDispatch } from "react-redux";
import { startLoading, endLoading } from "@src/reducers/loading";
import { getColumns } from "./NoticeListColumns";

function NoticeList() {
    const navigate = useNavigate();
    const [data, setData] = useState<NoticeType[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const dispatch = useDispatch();
    const [params, setParams] = useState({ page: 1, limit: 10 });
    const {
        isLoading,
        data: noticeData = { result: [], total: 0 },
        isError,
        refetch,
    } = useNoticeFetchData(params.page !== 0 ? params : {});

    useEffect(() => {
        if (isError) {
            // error
        } else {
            if (!isLoading && !isError && noticeData) {
                setData(noticeData.result);
                setTotalCount(noticeData.total);
                dispatch(endLoading());
            } else if (isError) {
                dispatch(endLoading());
            } else {
                dispatch(startLoading());
            }
        }
    }, [noticeData.result]);

    const { mutate: deleteMutate, mutateAsync: deleteMutateAsync } = useDeleteNotice();
    const onDelete = (id: number) => {
        setIsModalShow((prev) => !prev);
        deleteMutate(id, {
            onSuccess: () => {
                setTotalCount(totalCount - 1);
            },
            onError: (error) => {
                console.log(error);
            },
            onSettled: () => refetch(),
        });
    };
    const [deleteNo, setDeleteNo] = useState(0);
    const [isModalShow, setIsModalShow] = useState(false);
    const columns = useMemo(
        () => getColumns({ navigate, setDeleteNo, setIsModalShow, page: params.page, pageSize: params.limit }),
        [navigate, params.page, params.limit],
    );

    // 다중 선택 삭제
    const [selectedRows, setSelectedRows] = useState<NoticeType[]>([]);
    const [selectionClear, setSelectionClear] = useState(false);
    const [isBulkModalShow, setIsBulkModalShow] = useState(false);

    // PageTable이 매 렌더마다 호출하므로 실제 선택이 바뀐 경우에만 상태를 갱신 (무한 렌더 방지)
    const onSelectedRowsChange = useCallback((rows: any) => {
        const next: NoticeType[] = rows.map((r: any) => r.original);
        setSelectedRows((prev) => {
            const prevKey = prev.map((p) => p.viewControl).join(",");
            const nextKey = next.map((n) => n.viewControl).join(",");
            return prevKey === nextKey ? prev : next;
        });
    }, []);

    const onBulkDelete = async () => {
        setIsBulkModalShow(false);
        const targets = selectedRows.map((row) => row.viewControl);
        try {
            await Promise.all(targets.map((id) => deleteMutateAsync(id)));
        } catch (error) {
            console.log(error);
        } finally {
            refetch();
            setSelectedRows([]);
            setSelectionClear(true);
            setTimeout(() => {
                setSelectionClear(false);
            }, 10);
        }
    };

    return (
        <div css={listStyle}>
            <Title title="공지 사항" />
            <div css={searchDiv}>
                <div>현재 신규 업데이트 건수 : {data?.[0]?.updateCount ?? 0}건</div>
                <Button
                    color="red"
                    handleClick={() => {
                        if (selectedRows.length === 0) {
                            return;
                        }
                        setIsBulkModalShow(true);
                    }}
                    label={`선택 삭제${selectedRows.length > 0 ? ` (${selectedRows.length})` : ""}`}
                    size="medium"
                    isDisabled={selectedRows.length === 0}
                />
                <Button
                    color="gd"
                    handleClick={() => {
                        navigate("/help/notice/create");
                    }}
                    label="신규 등록"
                    size="medium"
                />
            </div>
            <div css={tableDiv}>
                <PageTable
                    initialState={{
                        pageIndex: 0,
                        pageSize: 10,
                        hiddenColumns: ["noticeNo"],
                    }}
                    columns={columns}
                    data={data}
                    totalCount={totalCount}
                    handlePageChange={(index: number) => {
                        const page = index + 1;
                        setParams({ page, limit: 10 });
                    }}
                    FixedHeight={700}
                    showTotalCount={true}
                    showCheckbox={true}
                    showNumber={false}
                    onSelectedRowsChange={onSelectedRowsChange}
                    selectedRows={{}}
                    selectionClear={selectionClear}
                    forcePage={params.page === 1}
                ></PageTable>
            </div>
            <ConfirmModal
                buttonText="삭제"
                handleCancel={() => setIsModalShow((prev) => !prev)}
                handleOk={() => {
                    onDelete(deleteNo);
                    setIsModalShow(false);
                }}
                isModalShow={isModalShow}
            >
                <>정말 삭제하시겠습니까?</>
            </ConfirmModal>
            <ConfirmModal
                buttonText="삭제"
                handleCancel={() => setIsBulkModalShow(false)}
                handleOk={onBulkDelete}
                isModalShow={isBulkModalShow}
            >
                <>선택한 {selectedRows.length}개의 공지를 삭제하시겠습니까?</>
            </ConfirmModal>
        </div>
    );
}

const listStyle = css`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 40px;
`;
const searchDiv = css`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
    margin-top: 20px;
`;
const tableDiv = css`
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-end;
    height: fit-content;
    td {
        padding: 10px 0;
        button:nth-of-type(1) {
            margin-right: 15px;
        }
    }
    .noticeContent {
        white-space: normal;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
    .noticeTitle {
        text-align: left;
    }
`;

export default NoticeList;
